// MembersPage — Plan 06-05 Task 3 (original Phase 6) + Plan 02.2-04 Task 3
// (Phase 2.2 extension).
//
// Phase 2.2 add-ons:
//   1. Dropdown subRole (RH/Jurídico/SESMT/Compras/Externo) — D-208 + eixo compras
//   2. Dropdown vendor type condicional quando subRole==='externo' — D-217
//   3. PdfUploadWidget embedded (PDF de nomeação obrigatório) — D-203/204/205
//   4. handleCreate envia body extension {subRole, externoVendorType?,
//      nomeacaoUpload: {r2Key, nonce, wrappedDekAdmin, wrappedDekEscrow,
//      filenameHash, filenameOriginal}} — Plan 02.2-02 finalize-as-part-of-create
//   5. KEK_admin_ti carregado de session via loadKekAdminTi + escrow pubkey
//      buscada de /api/admin/escrow/public-key (Phase 6 cofre infra)
//
// ADM-04 paridade: subRole/vendor type/PDF metadata gravados server-side
// como UserRecord fields; admin SPA NÃO decifra denúncia — apenas surface
// o firstAccessToken + otpAuthUri retornados pelo endpoint Phase 2.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  Textarea,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  tabId,
  tabPanelId,
  Badge,
  StatusDot,
} from '@aions/ui-primitives';
import type { BadgeTone } from '@aions/ui-primitives';
import { apiFetch } from '../lib/api-client';
import { PdfUploadWidget } from '../components/PdfUploadWidget';
import { FaceCaptureWidget } from '../components/FaceCaptureWidget';
import { CredentialResetPending } from '../components/CredentialResetPending';
import { loadKekAdminTi } from '../lib/kek-admin-session';
import { firstAccessUrl } from '../lib/spa-urls';
import { useSession } from '../contexts/SessionContext';
import type { NomeacaoUploadResult } from '../lib/nomeacao-upload';

const ROLES = ['comite_membro', 'comite_recurso', 'admin_ti', 'cofre_holder'] as const;
type Role = (typeof ROLES)[number];

const SUB_ROLES = ['rh', 'juridico', 'sesmt', 'compras', 'externo'] as const;
type SubRole = (typeof SUB_ROLES)[number];

const VENDOR_TYPES = ['auditoria_independente', 'juridico_externo', 'outro'] as const;
type VendorType = (typeof VENDOR_TYPES)[number];

/* Seções da tela, uma por vez. A navegação é o primitivo <Tabs> (WAI-ARIA
   tablist: roving tabindex, ←/→, Home/End) só ESTILIZADO como cards — os quatro
   painéis são mutuamente exclusivos, que é exatamente a semântica de tab. Cards
   em vez de abas porque cada seção tem um NÚMERO que vale ler sem abrir
   ("2 aguardando aprovação", "5 de 6 com rosto"): o seletor virou painel de
   estado, não enfeite. Se um dia forem só rótulos, volte para as abas padrão. */
const PANELS = ['adicionar', 'facial', 'credenciais', 'usuarios'] as const;
type PanelKey = (typeof PANELS)[number];
const NAV_ID_PREFIX = 'members';

/* Tom do badge de cada perfil. A cor CARREGA informação, não decora:
   `cofre_holder` é o break-glass (âmbar = atenção), `admin_ti` é infra sem
   acesso a conteúdo (info), e os dois papéis de comitê — os únicos que leem
   denúncia — ficam com o azul de marca. Papel desconhecido cai em neutral, que
   é o certo: perfil novo no backend aparece legível, sem cor inventada. */
const ROLE_TONE: Readonly<Record<string, BadgeTone>> = {
  comite_membro: 'brand',
  comite_recurso: 'brand',
  admin_ti: 'info',
  cofre_holder: 'amber',
};

/* Título de cada card reaproveita a chave do próprio painel — a copy não pode
   divergir entre o card e o cabeçalho da seção. `Record<PanelKey, …>` é
   exaustivo por construção: painel novo sem título reprova no tsc. */
const NAV_TITLE_KEY: Record<PanelKey, string> = {
  adicionar: 'members.create',
  facial: 'members.enrollment.title',
  credenciais: 'members.credreset.pending.title',
  usuarios: 'members.nav.usuarios',
};

interface AdminUser {
  id: string;
  email: string;
  roles: string[];
  subRole?: string;
  active: boolean;
  createdAt: number;
  /** Lockout de LOGIN (5 senhas erradas / 15 min, auth.ts). NÃO é o lockout
   *  biométrico — esse vive no NONCES_KV (liveness-lockout.ts) e não é exposto
   *  por endpoint algum do admin. A API já devolvia este campo; a SPA só não
   *  lia. Ver nota no bloco de ações da tabela. */
  lockedUntil?: number;
}

interface CreatedUser {
  userId: string;
  email: string;
  roles: string[];
  firstAccessToken: string;
  otpAuthUri: string;
}

// F1 Parte 2 — pré-flight do enforcement (ADR-0009 Peça 3). Resposta de
// GET /api/admin/users/liveness/enrollment-status. NÃO expõe template/conteúdo
// — só presença por membro.
interface EnrollmentStatus {
  ready: boolean;
  totalActiveMembers: number;
  enrolled: number;
  missing: { id: string; email: string }[];
}

export function MembersPage(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useSession();
  const jwtToken = session?.token;
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newRoles, setNewRoles] = useState<Role[]>(['comite_membro']);
  const [newSubRole, setNewSubRole] = useState<SubRole>('rh');
  const [newVendorType, setNewVendorType] = useState<VendorType>('auditoria_independente');
  const [pendingNomeacao, setPendingNomeacao] = useState<NomeacaoUploadResult | null>(null);
  const [kekAdminTi, setKekAdminTi] = useState<Uint8Array | null>(null);
  const [escrowPubKey, setEscrowPubKey] = useState<CryptoKey | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CreatedUser | null>(null);
  // F1 Parte 3 — feedback de "copiado" para o link/URI de primeiro acesso.
  const [copied, setCopied] = useState<'link' | 'otp' | null>(null);
  // ADR-0009 — membro selecionado para cadastro do template de liveness.
  const [enrollFor, setEnrollFor] = useState<AdminUser | null>(null);
  // F1 Parte 1 — destrave de lockout de liveness (ADR-0009 Peça 7).
  const [overrideFor, setOverrideFor] = useState<AdminUser | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  // Aviso de sucesso transiente (ex.: destrave concluído) — separado de `error`.
  const [notice, setNotice] = useState<string | null>(null);
  // F2 Parte C2 — abertura do pedido de reset pesado (dois admins).
  const [resetFor, setResetFor] = useState<AdminUser | null>(null);
  const [resetReason, setResetReason] = useState('');
  const [resetTotp, setResetTotp] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  // Sinaliza ao painel de pendentes para recarregar após abrir um pedido.
  const [credResetReload, setCredResetReload] = useState(0);
  // F1 Parte 2 — pré-flight de enrollment de liveness.
  const [enrollStatus, setEnrollStatus] = useState<EnrollmentStatus | null>(null);
  // 404 = LIVENESS_ENABLED != 'true' (ex.: produção hoje) → degrada limpo, sem erro.
  const [enrollStatusUnavailable, setEnrollStatusUnavailable] = useState(false);
  // Seção visível. Default 'adicionar': é a ação primária da tela, e o estado das
  // outras três já é legível nos cards sem abrir ("12 de 15 ativos", "5 de 6 com
  // rosto", "2 aguardando aprovação"). Trocar o default é uma linha.
  const [activePanel, setActivePanel] = useState<PanelKey>('adicionar');
  // Contagem de pendências vinda do painel de credenciais, só para o card de
  // navegação. setState é referência estável → não realimenta o efeito de lá.
  const [credResetCount, setCredResetCount] = useState(0);

  // Load KEK_admin_ti from session storage + escrow public key on mount.
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      if (!jwtToken) return;
      try {
        const kek = await loadKekAdminTi();
        if (!cancelled) setKekAdminTi(kek);
      } catch {
        if (!cancelled) setError(t('members.error_kek_missing'));
      }
      try {
        const resp = await apiFetch('/api/admin/escrow/public-key');
        if (resp.ok) {
          const { jwk } = (await resp.json()) as { jwk: JsonWebKey };
          const pubKey = await crypto.subtle.importKey(
            'jwk',
            jwk,
            { name: 'RSA-OAEP', hash: 'SHA-256' },
            false,
            ['encrypt'],
          );
          if (!cancelled) setEscrowPubKey(pubKey);
        } else if (!cancelled) {
          setError(t('members.error_escrow_pubkey_missing'));
        }
      } catch {
        if (!cancelled) setError(t('members.error_escrow_pubkey_missing'));
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [jwtToken, t]);

  const reload = useCallback(async (): Promise<void> => {
    try {
      const resp = await apiFetch('/api/admin/users');
      if (!resp.ok) {
        setError(t('common.error_unknown'));
        return;
      }
      const data = (await resp.json()) as { users: AdminUser[] };
      setUsers(data.users);
      setError(null);
    } catch {
      setError(t('common.error_network'));
    }
  }, [t]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // F1 Parte 2 — carrega o pré-flight de enrollment. 404 = liveness desabilitado
  // no ambiente (degrada limpo, não polui com erro). Read-only; não expõe template.
  const loadEnrollStatus = useCallback(async (): Promise<void> => {
    try {
      const resp = await apiFetch('/api/admin/users/liveness/enrollment-status');
      if (resp.status === 404) {
        setEnrollStatusUnavailable(true);
        setEnrollStatus(null);
        return;
      }
      setEnrollStatusUnavailable(false);
      if (!resp.ok) {
        setEnrollStatus(null);
        return;
      }
      setEnrollStatus((await resp.json()) as EnrollmentStatus);
    } catch {
      // Rede — não bloqueia a página; apenas não mostra o painel.
      setEnrollStatus(null);
    }
  }, []);

  useEffect(() => {
    void loadEnrollStatus();
  }, [loadEnrollStatus]);

  // Abre o painel de enroll para um membro faltante (pré-flight → ação).
  function enrollMissing(id: string): void {
    const user = users.find((u) => u.id === id);
    setEnrollFor(user ?? { id, email: id, roles: ['comite_membro'], active: true, createdAt: 0 });
  }

  async function handleCreate(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!newEmail.trim() || newRoles.length === 0) {
      setError(t('common.error_unknown'));
      return;
    }
    // TODO(URGENTE-PRE-PROD): PDF de nomeação tornado OPCIONAL a pedido do jurídico SÓ PARA DEMO.
    // Lei 14.457 / ADR-0001 exige o documento de nomeação. RESTAURAR obrigatoriedade ANTES
    // de produção real com denúncias. Decisão: jurídico Contato, demo 2026-06-19. Removido por: Vergueiro.
    // (guard `if (!pendingNomeacao) return` removido aqui — criar sem PDF é permitido temporariamente)
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        email: newEmail.trim().toLowerCase(),
        roles: newRoles,
        subRole: newSubRole,
        // PDF opcional na demo (TODO URGENTE-PRE-PROD acima) — só envia se houver upload.
        ...(pendingNomeacao ? { nomeacaoUpload: pendingNomeacao } : {}),
      };
      if (newSubRole === 'externo') {
        body.externoVendorType = newVendorType;
      }
      const resp = await apiFetch('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const errBody = (await resp.json().catch(() => ({}))) as { error?: string };
        // Mapeia o código do back para mensagem pt-BR; código não-mapeado cai no
        // genérico (nunca renderiza o código técnico cru — 🐞 QA-front #3).
        setError(
          errBody.error
            ? t(`members.errors.${errBody.error}`, { defaultValue: t('common.error_unknown') })
            : t('common.error_unknown'),
        );
        return;
      }
      const data = (await resp.json()) as CreatedUser;
      setCreated(data);
      setNewEmail('');
      setNewRoles(['comite_membro']);
      setNewSubRole('rh');
      setPendingNomeacao(null);
      await reload();
    } catch {
      setError(t('common.error_network'));
    } finally {
      setSubmitting(false);
    }
  }

  // F1 Parte 3 — copia link/URI para a área de transferência (best-effort).
  async function copyText(text: string, which: 'link' | 'otp'): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(which);
    } catch {
      // Clipboard indisponível (permissão/contexto não-seguro) — admin copia manualmente.
    }
  }

  // F1 Parte 1 — destrava o lockout de liveness do membro (POST .../liveness/override).
  // Motivo é obrigatório (auditado server-side). Reset é idempotente; não é bypass —
  // o membro ainda precisa passar no liveness. Backend já existe (admin/index.ts).
  async function handleOverride(): Promise<void> {
    if (!overrideFor) return;
    const reason = overrideReason.trim();
    if (!reason) return; // botão já desabilitado; defesa extra contra 400 reason_required
    setOverrideSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const resp = await apiFetch(`/api/admin/users/${overrideFor.id}/liveness/override`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        const known = ['reason_required', 'not_committee_member', 'user_not_found', 'not_found'];
        const key = body.error && known.includes(body.error) ? body.error : 'generic';
        setError(t(`members.liveness.override.errors.${key}`));
        return;
      }
      const data = (await resp.json()) as { ok: boolean; expiresAt: number };
      const time = new Date(data.expiresAt).toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
      setNotice(t('members.liveness.override.success', { email: overrideFor.email, time }));
      setOverrideFor(null);
      setOverrideReason('');
    } catch {
      setError(t('members.liveness.override.errors.network'));
    } finally {
      setOverrideSubmitting(false);
    }
  }

  // F2 Parte C2 — abre o pedido de reset pesado (cria = aprovação #1 do admin atual).
  // Exige motivo + TOTP do próprio admin. NÃO reseta nada: só após o 2º admin aprovar.
  async function handleCreateReset(): Promise<void> {
    if (!resetFor) return;
    const reason = resetReason.trim();
    if (!reason || !resetTotp.trim()) return;
    setResetSubmitting(true);
    setError(null);
    setNotice(null);
    try {
      const resp = await apiFetch('/api/admin/credential-reset', {
        method: 'POST',
        body: JSON.stringify({
          subjectUserId: resetFor.id,
          reason,
          totpCode: resetTotp.trim(),
        }),
      });
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        const known = [
          'invalid_totp',
          'self_reset_forbidden',
          'not_committee_member',
          'subject_not_found',
        ];
        const key = body.error && known.includes(body.error) ? body.error : 'generic';
        setError(t(`members.credreset.errors.${key}`));
        return;
      }
      setNotice(t('members.credreset.created', { email: resetFor.email }));
      setResetFor(null);
      setResetReason('');
      setResetTotp('');
      setCredResetReload((n) => n + 1); // recarrega o painel de pendentes
    } catch {
      setError(t('members.credreset.errors.generic'));
    } finally {
      setResetSubmitting(false);
    }
  }

  /** ADR-0021 §7 — desativação. Usa `POST /deactivate`, não `DELETE /users/:id`:
   *  o verbo antigo mentia (nunca houve remoção física — invariante 1 do ADR) e
   *  a API o mantém apenas como alias deprecado durante a janela de deploy. */
  async function handleDeactivate(id: string): Promise<void> {
    try {
      const resp = await apiFetch(`/api/admin/users/${id}/deactivate`, { method: 'POST' });
      if (!resp.ok) {
        setError(t('common.error_unknown'));
        return;
      }
      await reload();
    } catch {
      setError(t('common.error_network'));
    }
  }

  /** ADR-0021 — reativação. O 422 de composição não acontece aqui (reativar só
   *  melhora a composição), mas o erro é surfaceado para não falhar em silêncio. */
  async function handleActivate(u: AdminUser): Promise<void> {
    try {
      const resp = await apiFetch(`/api/admin/users/${u.id}/activate`, { method: 'POST' });
      if (!resp.ok) {
        setError(t('common.error_unknown'));
        return;
      }
      const body = (await resp.json().catch(() => ({}))) as { alreadyActive?: boolean };
      // Distinguir "reativei" de "já estava ativa": avisar sucesso de uma ação
      // que não aconteceu é o tipo de mentira pequena que corrói a confiança
      // na tela — e aqui a tela é a interface de uma trilha de auditoria.
      setNotice(
        body.alreadyActive
          ? t('members.activate.already', { email: u.email })
          : t('members.activate.success', { email: u.email }),
      );
      await reload();
    } catch {
      setError(t('common.error_network'));
    }
  }

  function toggleRole(r: Role): void {
    if (newRoles.includes(r)) {
      setNewRoles(newRoles.filter((x) => x !== r));
    } else {
      setNewRoles([...newRoles, r]);
    }
  }

  // F5 item 4 — link de primeiro acesso apontando para a SPA do PAPEL do membro
  // criado (created.roles); ver spa-urls.ts. admin_ti→admin, comite_recurso→recurso,
  // comite_membro→comitê.
  const firstAccessLink = created
    ? firstAccessUrl(created.roles, created.firstAccessToken, created.email, created.otpAuthUri)
    : null;

  // TODO(URGENTE-PRE-PROD): PdfUploadWidget ESCONDIDO para a demo (jurídico Contato, 2026-06-19,
  // por Vergueiro). O caminho de upload tem bug de ordem (sign-url 404 user_not_found — Opção A
  // deferida); esconder = "impossível clicar" > "espero que ninguém clique". RESTAURAR: trocar
  // `false` por `true` JUNTO com a obrigatoriedade do PDF e o fix do wiring, antes de prod real.
  // Tipado `: boolean` de propósito (não `false` literal) p/ preservar a narrowing de kek/escrow.
  const SHOW_PDF_WIDGET_DEMO: boolean = false;

  /* Linha de estado de cada card — é o que justifica card em vez de aba: o
     número aparece sem abrir a seção. */
  const facialStatus = enrollStatusUnavailable
    ? t('members.nav.facial_unavailable')
    : enrollStatus && enrollStatus.totalActiveMembers > 0
      ? t('members.nav.facial_count', {
          enrolled: enrollStatus.enrolled,
          total: enrollStatus.totalActiveMembers,
        })
      : t('members.nav.facial_none');

  const activeUsers = users.filter((u) => u.active).length;

  const now = Date.now();

  const navStatus: Record<PanelKey, string> = {
    adicionar: t('members.nav.adicionar_hint'),
    facial: facialStatus,
    credenciais:
      credResetCount > 0
        ? t('members.nav.credenciais_count', { count: credResetCount })
        : t('members.nav.credenciais_zero'),
    usuarios: t('members.nav.usuarios_count', { active: activeUsers, total: users.length }),
  };

  const navTabs = PANELS.map((value) => ({
    value,
    label: (
      <>
        <span className="members-nav__title">{t(NAV_TITLE_KEY[value])}</span>
        <span className="members-nav__status">{navStatus[value]}</span>
      </>
    ),
  }));

  /** Envelope de tabpanel — casa os ids com o botão do <Tabs> (WCAG). */
  function panelProps(key: PanelKey): {
    role: 'tabpanel';
    id: string;
    'aria-labelledby': string;
    tabIndex: 0;
  } {
    return {
      role: 'tabpanel',
      id: tabPanelId(key, NAV_ID_PREFIX),
      'aria-labelledby': tabId(key, NAV_ID_PREFIX),
      tabIndex: 0,
    };
  }

  return (
    <section className="members-page">
      <h1>{t('members.title')}</h1>

      {error && (
        <Alert variant="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {notice && (
        <Alert
          variant="success"
          role="status"
          data-testid="members-notice"
          style={{ marginBottom: 16 }}
        >
          {notice}
        </Alert>
      )}

      <Tabs
        className="members-nav"
        tabs={navTabs}
        value={activePanel}
        onValueChange={setActivePanel}
        aria-label={t('members.nav.aria_label')}
        idPrefix={NAV_ID_PREFIX}
      />

      {activePanel === 'adicionar' && (
        <div {...panelProps('adicionar')}>
          <Card>
            <h2>{t('members.create')}</h2>
            <form
              className="members-form"
              onSubmit={(e) => void handleCreate(e)}
              aria-label={t('members.create')}
            >
              <Field label={t('members.label.email')}>
                {({ id }) => (
                  <Input
                    id={id}
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    required
                  />
                )}
              </Field>

              <fieldset className="cf-field cf-field--fieldset">
                <legend className="cf-field__label">{t('members.label.roles')}</legend>
                <div className="cf-checkgroup">
                  {ROLES.map((r) => (
                    <label key={r} className="cf-checkgroup__option">
                      <input
                        type="checkbox"
                        checked={newRoles.includes(r)}
                        onChange={() => toggleRole(r)}
                      />
                      <span>{t(`members.role.${r}`)}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Phase 2.2 D-208 — subRole dropdown */}
              <Field label={t('members.subrole.label')} help={t('members.subrole.glossary')}>
                {({ id, describedBy }) => (
                  <select
                    id={id}
                    aria-describedby={describedBy}
                    className="cf-input"
                    value={newSubRole}
                    onChange={(e) => setNewSubRole(e.target.value as SubRole)}
                    data-testid="subrole-select"
                  >
                    {SUB_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {t(`members.subrole.${r}`)}
                      </option>
                    ))}
                  </select>
                )}
              </Field>

              {/* Phase 2.2 D-217 — vendor type condicional para externo */}
              {newSubRole === 'externo' && (
                <div data-testid="vendor-type-group">
                  <Field label={t('members.vendor.label')}>
                    {({ id }) => (
                      <select
                        id={id}
                        className="cf-input"
                        value={newVendorType}
                        onChange={(e) => setNewVendorType(e.target.value as VendorType)}
                        data-testid="vendor-type-select"
                      >
                        {VENDOR_TYPES.map((v) => (
                          <option key={v} value={v}>
                            {t(`members.vendor.${v}`)}
                          </option>
                        ))}
                      </select>
                    )}
                  </Field>
                </div>
              )}

              {/* Phase 2.2 ADM-01 — PdfUploadWidget embedded (ESCONDIDO na demo; ver SHOW_PDF_WIDGET_DEMO acima) */}
              {SHOW_PDF_WIDGET_DEMO && kekAdminTi && escrowPubKey && (
                <PdfUploadWidget
                  userId={newEmail.trim() || 'pending'}
                  kekAdminTi={kekAdminTi}
                  escrowPubKey={escrowPubKey}
                  onUploadComplete={(result) => setPendingNomeacao(result)}
                />
              )}

              {pendingNomeacao && (
                <Alert
                  variant="success"
                  data-testid="pdf-pending-success"
                  style={{ marginBottom: 16 }}
                >
                  {t('members.nomeacao.pending_ready')}
                </Alert>
              )}

              <Button type="submit" loading={submitting}>
                {submitting ? t('members.submitting') : t('members.create')}
              </Button>
            </form>

            {created && firstAccessLink && (
              <Alert
                variant="success"
                role="status"
                data-testid="created-success"
                style={{ marginTop: 16 }}
              >
                <p>
                  <strong>{t('members.created.title')}</strong>
                </p>
                <p>{t('members.created.instructions')}</p>

                <p className="cf-field__label" style={{ marginBottom: 4 }}>
                  {t('members.created.link_label')}
                </p>
                <p style={{ wordBreak: 'break-all' }}>
                  <a href={firstAccessLink} data-testid="first-access-link">
                    {firstAccessLink}
                  </a>
                </p>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void copyText(firstAccessLink, 'link')}
                    data-testid="copy-first-access-link"
                  >
                    {copied === 'link'
                      ? t('members.created.copied')
                      : t('members.created.copy_link')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void copyText(created.otpAuthUri, 'otp')}
                    data-testid="copy-otp-uri"
                  >
                    {copied === 'otp' ? t('members.created.copied') : t('members.created.copy_otp')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCreated(null);
                      setCopied(null);
                    }}
                  >
                    {t('common.close')}
                  </Button>
                </div>
              </Alert>
            )}
          </Card>
        </div>
      )}

      {activePanel === 'facial' && (
        <div {...panelProps('facial')}>
          {/* F1 Parte 2 — pré-flight: todos os membros têm rosto cadastrado antes do enforce? */}
          <Card data-testid="enrollment-status">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>{t('members.enrollment.title')}</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void loadEnrollStatus()}
                data-testid="enrollment-recheck"
              >
                {t('members.enrollment.recheck')}
              </Button>
            </div>

            {enrollStatusUnavailable && (
              <p className="cf-field__help" style={{ marginTop: 12 }}>
                {t('members.enrollment.unavailable')}
              </p>
            )}

            {enrollStatus && enrollStatus.totalActiveMembers === 0 && (
              <p className="cf-field__help" style={{ marginTop: 12 }}>
                {t('members.enrollment.none_active')}
              </p>
            )}

            {enrollStatus && enrollStatus.totalActiveMembers > 0 && enrollStatus.ready && (
              <Alert
                variant="success"
                role="status"
                data-testid="enrollment-ready"
                style={{ marginTop: 12 }}
              >
                {t('members.enrollment.ready', { total: enrollStatus.totalActiveMembers })}
              </Alert>
            )}

            {enrollStatus && enrollStatus.totalActiveMembers > 0 && !enrollStatus.ready && (
              <div data-testid="enrollment-incomplete" style={{ marginTop: 12 }}>
                <p>
                  {t('members.enrollment.incomplete', {
                    enrolled: enrollStatus.enrolled,
                    total: enrollStatus.totalActiveMembers,
                  })}
                </p>
                <ul>
                  {enrollStatus.missing.map((m) => (
                    <li key={m.id} style={{ marginBottom: 4 }}>
                      {m.email}{' '}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => enrollMissing(m.id)}
                        data-testid={`enrollment-missing-${m.id}`}
                        style={{ marginLeft: 8 }}
                      >
                        {t('members.enrollment.enroll_cta')}
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* F2 Parte C2 — pedidos de reset pesado pendentes (lado da 2ª aprovação).
          Montado SEMPRE (não só na seção ativa) porque é ele quem busca a
          contagem que alimenta o card de navegação; escondido por CSS quando
          fora da seção. Desmontar zeraria o número até o admin abrir a aba. */}
      <div {...panelProps('credenciais')} hidden={activePanel !== 'credenciais'}>
        <CredentialResetPending reloadSignal={credResetReload} onCountChange={setCredResetCount} />
      </div>

      {activePanel === 'usuarios' && (
        <div {...panelProps('usuarios')}>
          <Card className="members-users">
            <div className="members-users__head">
              <h2>{t('members.nav.usuarios')}</h2>
              <StatusDot
                tone={activeUsers > 0 ? 'success' : 'neutral'}
                label={t('members.nav.usuarios_count', {
                  active: activeUsers,
                  total: users.length,
                })}
              />
            </div>
            <Table cards>
              <Thead>
                <Tr>
                  <Th>{t('members.label.email')}</Th>
                  <Th>{t('members.label.roles')}</Th>
                  <Th>Ativo</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {users.length === 0 ? (
                  <Tr>
                    <Td colSpan={4}>{t('common.no_data')}</Td>
                  </Tr>
                ) : (
                  users.map((u) => (
                    <Tr key={u.id} data-inactive={u.active ? undefined : ''}>
                      <Td mono data-label={t('members.label.email')}>{u.email}</Td>
                      <Td data-label={t('members.label.roles')}>
                        {/* Era `u.roles.join(', ')` — despejava o enum do backend
                          ("comite_membro, cofre_holder") na tela. Badge com o
                          rótulo já traduzido (members.role.*, as MESMAS chaves do
                          formulário de criação). */}
                        <span className="members-users__roles">
                          {u.roles.map((r) => (
                            <Badge key={r} tone={ROLE_TONE[r] ?? 'neutral'}>
                              {t(`members.role.${r}`, { defaultValue: r })}
                            </Badge>
                          ))}
                        </span>
                      </Td>
                      <Td data-label="Ativo">
                        <StatusDot
                          tone={u.active ? 'success' : 'neutral'}
                          label={u.active ? t('common.yes') : t('common.no')}
                        />
                      </Td>
                      <Td>
                        {/* Ações agrupadas: as três de manutenção do membro à
                          esquerda, e o DESATIVAR empurrado para a direita
                          (`margin-left:auto` no CSS) — separar o destrutivo do
                          resto reduz clique errado numa linha densa.

                          ⚠️ Sobre as cores: LARANJA (falta cadastrar rosto) sai
                          do pré-flight, que devolve exatamente quem está sem
                          template. AMARELO marca `lockedUntil`, que é o lockout
                          de LOGIN (auth.ts, 5 senhas erradas) — NÃO o lockout
                          biométrico, que vive no NONCES_KV e não é exposto por
                          endpoint nenhum do admin. Pintar o botão de destrave
                          biométrico com o sinal de login seria dar cor a um
                          estado que a tela não conhece. */}
                        <div className="members-actions">
                          {/* O "Cadastrar rosto" SAIU desta linha. Ele duplicava
                              a seção Cadastro facial, que já lista exatamente
                              quem está sem template e traz o CTA ao lado de cada
                              um — a lista de faltantes é a fonte, e o botão aqui
                              obrigava o admin a saber de cor quem precisava.
                              Consequência assumida: a marca LARANJA de "falta
                              cadastrar rosto" não aparece mais nesta tabela; o
                              sinal vive na seção facial. */}
                          {u.active && u.roles.includes('comite_membro') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className={
                                u.lockedUntil && u.lockedUntil > now
                                  ? 'members-btn--attention'
                                  : undefined
                              }
                              onClick={() => {
                                setOverrideFor(u);
                                setOverrideReason('');
                              }}
                              data-testid={`override-${u.id}`}
                            >
                              {t('members.liveness.override.action')}
                            </Button>
                          )}
                          {u.active && u.roles.includes('comite_membro') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setResetFor(u);
                                setResetReason('');
                                setResetTotp('');
                              }}
                              data-testid={`credreset-open-${u.id}`}
                            >
                              {t('members.credreset.action')}
                            </Button>
                          )}
                          {u.active && (
                            <Button
                              variant="danger"
                              size="sm"
                              className="members-actions__destructive"
                              onClick={() => void handleDeactivate(u.id)}
                            >
                              {t('members.button.deactivate')}
                            </Button>
                          )}
                          {/* ADR-0021 — a linha inativa deixa de ser beco sem
                              saída. Antes não havia ação nenhuma aqui, e o único
                              caminho de volta era SQL, fora da trilha. */}
                          {!u.active && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="members-actions__destructive"
                              onClick={() => void handleActivate(u)}
                              data-testid={`activate-${u.id}`}
                            >
                              {t('members.button.activate')}
                            </Button>
                          )}
                        </div>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </Card>
        </div>
      )}

      {/* ADR-0009 — painel de cadastro do template de liveness do membro selecionado.
          FORA das seções de propósito: é acionável pela lista de usuários E pelo
          card de rosto (lista "faltam cadastrar"), então tem de sobreviver à
          troca de seção. O mesmo vale para os painéis de destrave e de reset. */}
      {enrollFor && (
        <Card data-testid="enroll-face-panel" style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>
              {t('members.liveness.action')} — {enrollFor.email}
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setEnrollFor(null)}
              data-testid="enroll-face-close"
            >
              {t('common.close')}
            </Button>
          </div>
          <FaceCaptureWidget
            key={enrollFor.id}
            userId={enrollFor.id}
            onEnrolled={() => {
              void reload();
              void loadEnrollStatus();
            }}
          />
        </Card>
      )}

      {/* F1 Parte 1 — painel de destrave de lockout de liveness (ADR-0009 Peça 7). */}
      {overrideFor && (
        <Card
          data-testid="override-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="override-title"
          style={{ marginTop: 16 }}
        >
          <h2 id="override-title" style={{ marginTop: 0 }}>
            {t('members.liveness.override.title', { email: overrideFor.email })}
          </h2>
          <p className="cf-field__help">{t('members.liveness.override.explain')}</p>
          <Field label={t('members.liveness.override.reason_label')}>
            {({ id }) => (
              <Textarea
                id={id}
                rows={3}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder={t('members.liveness.override.reason_placeholder')}
                data-testid="override-reason"
              />
            )}
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              disabled={!overrideReason.trim() || overrideSubmitting}
              loading={overrideSubmitting}
              onClick={() => void handleOverride()}
              data-testid="override-confirm"
            >
              {overrideSubmitting
                ? t('members.liveness.override.submitting')
                : t('members.liveness.override.confirm')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setOverrideFor(null);
                setOverrideReason('');
              }}
              disabled={overrideSubmitting}
              data-testid="override-cancel"
            >
              {t('members.liveness.override.cancel')}
            </Button>
          </div>
        </Card>
      )}

      {/* F2 Parte C2 — abertura do pedido de reset pesado (aprovação #1 do admin atual). */}
      {resetFor && (
        <Card
          data-testid="credreset-open-panel"
          role="dialog"
          aria-modal="false"
          aria-labelledby="credreset-open-title"
          style={{ marginTop: 16 }}
        >
          <h2 id="credreset-open-title" style={{ marginTop: 0 }}>
            {t('members.credreset.title', { email: resetFor.email })}
          </h2>
          <p className="cf-field__help">{t('members.credreset.explain')}</p>
          <Field label={t('members.credreset.reason_label')}>
            {({ id }) => (
              <Textarea
                id={id}
                rows={3}
                value={resetReason}
                onChange={(e) => setResetReason(e.target.value)}
                placeholder={t('members.credreset.reason_placeholder')}
                data-testid="credreset-reason"
              />
            )}
          </Field>
          <Field label={t('members.credreset.totp_label')}>
            {({ id }) => (
              <Input
                id={id}
                type="text"
                inputMode="numeric"
                mono
                value={resetTotp}
                onChange={(e) => setResetTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                data-testid="credreset-open-totp"
                style={{ maxWidth: 180 }}
              />
            )}
          </Field>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              disabled={!resetReason.trim() || !resetTotp.trim() || resetSubmitting}
              loading={resetSubmitting}
              onClick={() => void handleCreateReset()}
              data-testid="credreset-open-confirm"
            >
              {resetSubmitting ? t('members.credreset.submitting') : t('members.credreset.confirm')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setResetFor(null);
                setResetReason('');
                setResetTotp('');
              }}
              disabled={resetSubmitting}
              data-testid="credreset-open-cancel"
            >
              {t('members.credreset.cancel')}
            </Button>
          </div>
        </Card>
      )}
    </section>
  );
}
